import { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus } from 'lucide-react';
import type { AdrPerson, AdrPersonRole } from '../../types';
import { useEmployees, useEmployeeById } from '../../stores';

const ROLES: AdrPersonRole[] = ['author', 'responsible', 'reviewer'];

function PersonRow({ person, onRemove }: { person: AdrPerson; onRemove: () => void }) {
  const { t } = useTranslation();
  const employee = useEmployeeById(person.employeeId);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', backgroundColor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', marginBottom: 3 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: employee?.color || '#9ca3af', flexShrink: 0 }} />
      <span style={{ fontSize: 10, color: '#1f2937', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{employee?.name || person.employeeId}</span>
      <span style={{ fontSize: 9, color: '#fff', fontWeight: 500, padding: '1px 5px', backgroundColor: '#6b7280', borderRadius: 3, flexShrink: 0 }}>
        {t(`adr.person.${person.role}` as any)}
      </span>
      <button
        onClick={onRemove}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#9ca3af', flexShrink: 0 }}
      >
        <X size={10} />
      </button>
    </div>
  );
}

export const AdrPersonPicker = memo(function AdrPersonPicker({
  people,
  onChange,
}: {
  people: AdrPerson[];
  onChange: (people: AdrPerson[]) => void;
}) {
  const { t } = useTranslation();
  const employees = useEmployees();
  const [addOpen, setAddOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedRole, setSelectedRole] = useState<AdrPersonRole>('author');

  const handleAdd = () => {
    if (!selectedEmployee) return;
    // Check for duplicate
    if (people.some(p => p.employeeId === selectedEmployee && p.role === selectedRole)) return;
    onChange([...people, { employeeId: selectedEmployee, role: selectedRole }]);
    setSelectedEmployee('');
    setAddOpen(false);
  };

  const handleRemove = (index: number) => {
    onChange(people.filter((_, i) => i !== index));
  };

  return (
    <div>
      {people.map((person, i) => (
        <PersonRow key={`${person.employeeId}-${person.role}`} person={person} onRemove={() => handleRemove(i)} />
      ))}

      {addOpen ? (
        <div style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'center' }}>
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            style={{ flex: 1, fontSize: 10, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 4, backgroundColor: '#fff', color: '#1f2937' }}
          >
            <option value="">{t('adr.person.searchEmployee')}</option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>{emp.name}</option>
            ))}
          </select>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as AdrPersonRole)}
            style={{ fontSize: 10, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 4, backgroundColor: '#fff', color: '#1f2937' }}
          >
            {ROLES.map(role => (
              <option key={role} value={role}>{t(`adr.person.${role}` as any)}</option>
            ))}
          </select>
          <button
            onClick={handleAdd}
            disabled={!selectedEmployee}
            style={{ fontSize: 10, padding: '2px 8px', border: 'none', borderRadius: 4, background: '#3b82f6', color: '#fff', cursor: 'pointer', opacity: !selectedEmployee ? 0.4 : 1 }}
          >
            {t('common.add')}
          </button>
        </div>
      ) : (
        <button
          onClick={() => setAddOpen(true)}
          style={{ fontSize: 10, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, padding: '4px 0' }}
        >
          <Plus size={10} /> {t('adr.person.add')}
        </button>
      )}
    </div>
  );
});
